import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import AppDropdown from "../../components/AppDropdown/AppDropdown";
import * as apicall from "../../components/Apicall";
import { phoneCountryIso } from "../../utils/phoneValidation";
import { REASON_LABELS } from "./leadMeta";

const PRIORITY_OPTIONS = [
  { label: "Medium", value: "Medium" },
  { label: "High", value: "High" },
  { label: "Low", value: "Low" },
];

const emptyForm = {
  customerName: "", phone: "", email: "",
  // Channel (Web/App) is never picked here — the backend copies it
  // automatically from the linked booking. `reason` is required (see
  // canSubmit) and pre-filled to "payment_pending" below when opened from a
  // stalled-booking draft row.
  reason: "", priority: "Medium",
  relatedBooking: "", assignTo: "", note: "",
  pendingBookingId: "",
  serviceName: "", serviceType: "", serviceOffering: "", cartValue: "",
};

const sectionTag = { fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#C9194A", margin: "4px 0 12px" };
const fieldLabel = { display: "block", fontFamily: "'Inter', sans-serif", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 6 };

// Opened either blank (header "+ Create Lead" button) or with presetLead
// (a draft "no lead yet" row's own Create Lead action, from LeadsList.jsx) —
// that row already has everything needed, so this just prefills directly.
const CreateLeadDrawer = ({ show, onHide, onCreate, presetLead, access }) => {
  const [form, setForm] = useState(emptyForm);
  const [teamMembers, setTeamMembers] = useState([]);

  const currency = localStorage.getItem("currency") || "AED";

  useEffect(() => {
    if (!show) return;
    apicall.get("getVendorTeamUsers")
      .then((res) => setTeamMembers(res?.data || []))
      .catch(() => setTeamMembers([]));

    if (presetLead) {
      setForm((f) => ({
        ...f,
        customerName: presetLead.customerName || "",
        phone: (presetLead.phone || "").replace(/\D/g, ""),
        reason: "payment_pending",
        relatedBooking: presetLead.bookingId ? String(presetLead.bookingId) : "",
        pendingBookingId: presetLead.bookingId ? String(presetLead.bookingId) : "",
        serviceName: presetLead.service && presetLead.service !== "—" ? presetLead.service : "",
        serviceType: presetLead.serviceType || "",
        serviceOffering: presetLead.serviceOffering || "",
        cartValue: presetLead.cartValue ? String(presetLead.cartValue).replace(/[^\d.]/g, "") : "",
      }));
    }
  }, [show, presetLead]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  // AppDropdown (PrimeReact) hands back the new value as e.value, not e.target.value.
  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.value }));

  const canSubmit =
    form.customerName.trim() &&
    form.phone.trim() &&
    form.reason.trim() &&
    form.serviceName.trim() &&
    form.serviceType.trim() &&
    form.serviceOffering.trim() &&
    String(form.cartValue).trim() &&
    form.note.trim();

  const handleSubmit = () => {
    if (!canSubmit) return;
    const assignedMember = teamMembers.find((m) => String(m.id) === String(form.assignTo));
    onCreate({ ...form, assignToName: assignedMember?.name || assignedMember?.full_name || "" });
    setForm(emptyForm);
  };

  return (
    <SlidePanel
      show={show}
      onHide={onHide}
      title="Create Lead"
      width={480}
      footer={
        <>
          <button className="btn btn-outline-borders" onClick={onHide} style={{ height: 40, borderRadius: 9, padding: "0 18px" }}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{ height: 40, borderRadius: 9, padding: "0 18px", opacity: canSubmit ? 1 : 0.5 }}
          >
            Create Lead
          </button>
        </>
      }
    >
      <p style={{ fontSize: 12.5, color: "#9CA3AF", marginTop: -12, marginBottom: 20 }}>
        {presetLead ? `Creating a lead for ${presetLead.customerName}'s stalled booking` : "Manually log a customer who needs follow-up"}
      </p>

      <div style={sectionTag}>Customer</div>
      <div className="mb-3">
        <label style={fieldLabel}>Customer name <span style={{ color: "#C9194A" }}>*</span></label>
        <input className="form-control" placeholder="e.g. Anita Rao" value={form.customerName} onChange={set("customerName")} />
      </div>
      <div className="row g-3 mb-3">
        <div className="col-6">
          <label style={fieldLabel}>Phone number <span style={{ color: "#C9194A" }}>*</span></label>
          <PhoneInput
            country={phoneCountryIso(currency)}
            onlyCountries={["ae", "in"]}
            disableDropdown={true}
            countryCodeEditable={false}
            value={form.phone}
            onChange={(value) => setForm((f) => ({ ...f, phone: value.replace(/\D/g, "") }))}
            inputStyle={{ width: "100%", height: 40 }}
          />
        </div>
        <div className="col-6">
          <label style={fieldLabel}>Email</label>
          <input className="form-control" placeholder="Optional" value={form.email} onChange={set("email")} />
        </div>
      </div>

      <div style={{ ...sectionTag, marginTop: 6 }}>Lead details</div>
      <div className="row g-3 mb-3">
        <div className="col-6">
          <label style={fieldLabel}>Lead reason <span style={{ color: "#C9194A" }}>*</span></label>
          <AppDropdown
            value={form.reason}
            onChange={setField("reason")}
            options={Object.entries(REASON_LABELS).map(([value, label]) => ({ label, value }))}
            placeholder="Select a reason…"
          />
        </div>
        <div className="col-6">
          <label style={fieldLabel}>Priority</label>
          <AppDropdown value={form.priority} onChange={setField("priority")} options={PRIORITY_OPTIONS} />
        </div>
      </div>
      <div className="mb-3">
        <label style={fieldLabel}>Related booking / service (optional)</label>
        <input className="form-control" placeholder="Search booking ref or service…" value={form.relatedBooking} onChange={set("relatedBooking")} />
        <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 5 }}>Link this lead to an existing quote or booking, if any</div>
      </div>

      <div style={{ ...sectionTag, marginTop: 6 }}>Service details</div>
      <div className="mb-3">
        <label style={fieldLabel}>Service <span style={{ color: "#C9194A" }}>*</span></label>
        <input className="form-control" placeholder="e.g. Deep Cleaning" value={form.serviceName} onChange={set("serviceName")} />
      </div>
      <div className="row g-3 mb-3">
        <div className="col-6">
          <label style={fieldLabel}>Service type <span style={{ color: "#C9194A" }}>*</span></label>
          <input className="form-control" placeholder="e.g. Residential" value={form.serviceType} onChange={set("serviceType")} />
        </div>
        <div className="col-6">
          <label style={fieldLabel}>Service offering <span style={{ color: "#C9194A" }}>*</span></label>
          <input className="form-control" placeholder="e.g. 2 BHK Package" value={form.serviceOffering} onChange={set("serviceOffering")} />
        </div>
      </div>
      <div className="mb-3">
        <label style={fieldLabel}>Amount <span style={{ color: "#C9194A" }}>*</span></label>
        <input className="form-control" type="number" placeholder="e.g. 2450" value={form.cartValue} onChange={set("cartValue")} />
      </div>

      <div className="mb-3">
        <label style={fieldLabel}>Assign to</label>
        <AppDropdown
          value={form.assignTo}
          onChange={setField("assignTo")}
          options={[
            // Same self-claim shortcut as the Leads list/detail screens —
            // only for a team member creating a lead for themselves; the
            // vendor owner already sees every team member in the plain list
            // below and has no need for it.
            ...(access?.userId != null && !access?.canManageLeads
              ? [{ label: "Assign to me", value: access.userId }]
              : []),
            ...teamMembers.map((m) => ({ label: m.name || m.full_name, value: m.id })),
          ]}
          placeholder="Select Team Member"
        />
      </div>
      <div className="mb-3">
        <label style={fieldLabel}>Initial note <span style={{ color: "#C9194A" }}>*</span></label>
        <textarea
          className="form-control"
          style={{ height: 84, resize: "none" }}
          placeholder="What happened — e.g. asked for a callback about AC repair pricing"
          value={form.note}
          onChange={set("note")}
        />
      </div>
    </SlidePanel>
  );
};

export default CreateLeadDrawer;
